import React, { useState, useCallback, useEffect } from 'react'
import ReactDataGrid from '@inovua/reactdatagrid-community'
import { Alert } from "@aws-amplify/ui-react";
import '@inovua/reactdatagrid-community/index.css'
import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter'
import moment from "moment";
import { v4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from "aws-amplify";
import * as queries from '../graphql/queries';

const STATUSES = ['Approved', 'Pending'];
const BRANDS = ['Factor', 'Youfoodz'];
const COUNTRIES = ['AU', 'US'];

export default function CreditNotesDataGrid() {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);

  const addAlert = (alertMessage) => {
    setAlerts(prevAlerts => [...prevAlerts, 
      <Alert key={v4()} isDismissible={true} variation="error">{alertMessage}</Alert>
    ]);
  }

  async function fetchAllCreditNotes() {
    // Get all credit notes.  This could be paged instead, but then filters would need to be implemented differently.
    let nextToken = null;
    let allCreditNotes = [];
  
    do {
      const response = await API.graphql(graphqlOperation(queries.listCreditNotes, { 
        limit: 1000,
        nextToken: nextToken
      }));
  
      allCreditNotes = allCreditNotes.concat(response.data.listCreditNotes.items);
      nextToken = response.data.listCreditNotes.nextToken;
    } while (nextToken);
  
    return allCreditNotes;
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setAlerts([]);
      try {
        const creditNotes = await fetchAllCreditNotes();
        const dataSource = creditNotes.map(credit_note => {
          let brisbane_time = new Date(credit_note.created_at);
          brisbane_time.setHours(brisbane_time.getHours() + 10);
          return {
              ...credit_note,
              created_at: brisbane_time.toISOString().substring(0,10)
          };
        });        
        setDataSource(dataSource);
      } catch (error) {
        console.error("Error fetching data:", error);
        addAlert("Error fetching data: " + error.toString());
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const statuses = STATUSES.map(item => ({
    id: item,
    label: item
  }));

  const brands = BRANDS.map(item => ({
    id: item,
    label: item
  }));

  const countries = COUNTRIES.map(item => ({
    id: item,
    label: item
  }));

  const navigate = useNavigate();

  const gridStyle = { minHeight: 800 }
  const scrollProps = { autoHide: false };
  const defaultSortInfo = { name: 'created_at', dir: -1 }

  const filterValue = [
    { name: 'created_at', operator: 'eq', type: 'date', value: '' },
    { name: 'status', operator: 'eq', type: 'select', value: null },
    { name: 'customer_name', operator: 'contains', type: 'string', value: '' },
    { name: 'credit_note_number', operator: 'contains', type: 'string', value: '' },
    { name: 'order_number', operator: 'contains', type: 'string', value: '' },
    { name: 'created_by', operator: 'contains', type: 'string', value: '' },
    { name: 'approved_by', operator: 'contains', type: 'string', value: '' },
    { name: 'brand', operator: 'contains', type: 'string', value: null },
    { name: 'country', operator: 'contains', type: 'string', value: null },
  ];

  window.moment = moment
  const columns = [
    { name: 'id', header: 'ID', defaultFlex: 1, defaultVisible: false },
    {
      name: 'created_at',
      header: 'Created',
      defaultFlex: 1,
      filterEditor: DateFilter,
      filterEditorProps: (props, { index }) => {
        return {
          dateFormat: 'YYYY-MM-DD',
          cancelButton: false,
          highlightWeekends: false
        }
      },
      render: ({ value, cellProps }) => {
        return moment(value).format('YYYY-MM-DD')
      }
    },
    {
      name: 'status',
      header: 'Status',
      defaultFlex: 1,
      filterEditor: SelectFilter,
      filterEditorProps: {
        placeholder: 'All',
        dataSource: statuses
      },
    },
    { name: 'customer_name', defaultFlex: 1, header: 'Customer Name' },
    { name: 'credit_note_number', defaultFlex: 1, header: 'Credit Note Number' },
    { name: 'order_number', defaultFlex: 1, header: 'Order Number' },
    { name: 'created_by', defaultFlex: 1, header: 'Creator' },
    { name: 'approved_by', defaultFlex: 1, header: 'Approved by' },
    {
      name: 'brand',
      header: 'Brand',
      defaultFlex: 1,
      filterEditor: SelectFilter,
      filterEditorProps: {
        placeholder: 'All',
        dataSource: brands
      },
    },
    {
      name: 'country',
      header: 'Country',
      defaultFlex: 1,
      filterEditor: SelectFilter,
      filterEditorProps: {
        placeholder: 'All',
        dataSource: countries
      },
    }
  ];

  const onSelectionChange = useCallback(({ selected }) => {
    navigate('/edit-credit-note/' + selected)
  }, [navigate])

  return (
    <div>
      {alerts}
      <ReactDataGrid
        idProperty="id"
        style={gridStyle}
        scrollProps={scrollProps}
        defaultSortInfo={defaultSortInfo}
        onSelectionChange={onSelectionChange}
        enableSelection={true}
        defaultFilterValue={filterValue}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
      />
    </div>
  );
}